import React, { useCallback, useMemo } from 'react';

import { Icon } from '@rbilabs/universal-components';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { ProductDetailListItem } from 'components/product-detail-list-item';
import { ProductDetailListItemGroup } from 'components/product-detail-list-item-group';
import { FallbackImageSvg } from 'components/svgs/fallback-image';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useProductWizardFlow } from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';

import { useProductPropsSelections } from '../use-product-props-selections';

import { OrderSummaryContainer } from './styled';
import { transformUserSelectionsToSummaryItems } from './utils';

export const OrderSummary = () => {
  const { selectedProduct, userSelections } = useProductWizardContext();
  const { goToCustomizeProduct, id } = useProductWizardFlow();
  const { priceForComboSlotSelection } = useMenuContext();
  const { fontFamily: takeoverFontFamily } = useAppTakeoverPdp(id);

  const { buildProductPropsSelections } = useProductPropsSelections();
  const { formatCalories } = useMenuCalories();

  const mappedSummaryItems = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }
    return transformUserSelectionsToSummaryItems({
      buildProductPropsSelections,
      priceForComboSlotSelection,
      selectedProduct,
      userSelections,
    });
  }, [buildProductPropsSelections, priceForComboSlotSelection, selectedProduct, userSelections]);

  const showImages = mappedSummaryItems.some(
    item => !!item.productListItemProps.productProps.image
  );

  const handleSummaryLineClick = useCallback(
    (slotKey: string) => {
      goToCustomizeProduct({ slotKey, step: ProductWizardStep.ProductEditOrder });
    },
    [goToCustomizeProduct]
  );

  const logEventProductCustomization = (name: string) => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_ORDER_EDIT_ITEM,
        name,
      },
    });
  };

  if (!selectedProduct) {
    return null;
  }

  return (
    <OrderSummaryContainer testID="order-summary">
      <ProductDetailListItemGroup
        items={mappedSummaryItems.map(summaryItem => (
          <ProductDetailListItem
            key={summaryItem.slotKey}
            {...summaryItem.productListItemProps}
            productProps={{
              ...summaryItem.productListItemProps.productProps,
              fallbackImage: showImages ? FallbackImageSvg : undefined,
              calories: formatCalories(summaryItem.productListItemProps.productProps.calories),
              fontFamily: takeoverFontFamily,
            }}
            controls={
              summaryItem.productListItemProps.isClickable && (
                <Icon variant="next" testID="next-icon" />
              )
            }
            onNavigate={() => {
              handleSummaryLineClick(summaryItem.slotKey);
              logEventProductCustomization(
                summaryItem.productListItemProps.productProps.name ?? ''
              );
            }}
          />
        ))}
      />
    </OrderSummaryContainer>
  );
};
